import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, X, ArrowRight, Sparkles } from 'lucide-react';
import Fuse from 'fuse.js';

const API = '';

const SearchPalette = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [fuse, setFuse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { lang } = useParams();
  const prefix = `/${lang || 'en'}`;

  // Load catalog on first open
  useEffect(() => {
    if (!open || catalog.length > 0) return;
    setLoading(true);
    fetch(`${API}/api/catalog/products?limit=500`)
      .then(r => r.json())
      .then(data => {
        const products = data.products || [];
        setCatalog(products);
        setFuse(new Fuse(products, {
          keys: [
            { name: 'color_name', weight: 0.4 },
            { name: 'texture_variant', weight: 0.3 },
            { name: 'color_code', weight: 0.15 },
            { name: 'color_category', weight: 0.15 },
          ],
          threshold: 0.35,
          includeScore: true,
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, catalog.length]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setResults([]);
      setSelectedIdx(0);
    }
  }, [open]);

  // Search
  useEffect(() => {
    if (!fuse || !query.trim()) {
      setResults(query.trim() ? [] : catalog.slice(0, 8));
      setSelectedIdx(0);
      return;
    }
    const res = fuse.search(query).slice(0, 12).map(r => r.item);
    setResults(res);
    setSelectedIdx(0);
  }, [query, fuse, catalog]);

  const goTo = useCallback((product) => {
    const slug = product.texture_variant.toLowerCase().replace(/\s+/g, '-');
    navigate(`${prefix}/products/${slug}?color=${encodeURIComponent(product.color_code)}`, { state: { fromSearch: Date.now() } });
    onClose();
  }, [navigate, prefix, onClose]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIdx]) {
      goTo(results[selectedIdx]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="search-palette">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      {/* Palette */}
      <div className="relative max-w-2xl mx-4 sm:mx-auto mt-[8vh] sm:mt-[12vh] animate-in fade-in slide-in-from-top-4 duration-200">
        <div className="bg-[#0f0f0f] border border-[var(--text-primary)]/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden">
          {/* Input */}
          <div className="flex items-center gap-3 px-5 border-b border-[var(--text-primary)]/5">
            <Search className="w-5 h-5 text-[var(--gold)] flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search textures, shades, colors..."
              className="flex-1 bg-transparent text-[var(--text-primary)] text-base py-4 outline-none placeholder:text-[#666] border-none ring-0 focus:ring-0 focus:outline-none focus:border-none"
              style={{ WebkitAppearance: 'none', boxShadow: 'none' }}
              data-testid="search-input"
            />
            <div className="flex items-center gap-2">
              {query && (
                <button onClick={() => setQuery('')} className="text-[#666] hover:text-[var(--text-secondary)] transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
              <kbd className="hidden sm:inline-flex items-center text-[10px] text-[#666] border border-[var(--text-primary)]/10 rounded px-1.5 py-0.5">ESC</kbd>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="search-results">
            {loading ? (
              <div className="px-5 py-8 text-center">
                <div className="w-6 h-6 border-2 border-[var(--gold)]/30 border-t-[#C9A227] rounded-full animate-spin mx-auto mb-3" />
                <p className="text-xs text-[#666]">Loading catalog...</p>
              </div>
            ) : results.length === 0 && query.trim() ? (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-[var(--text-secondary)]">No results for "{query}"</p>
                <p className="text-xs text-[#666] mt-1">Try a different shade name, texture, or color code</p>
              </div>
            ) : (
              <>
                {!query.trim() && (
                  <div className="px-5 py-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]">
                      <Sparkles className="w-3 h-3 inline mr-1" />Popular
                    </p>
                  </div>
                )}
                {results.map((product, i) => (
                  <button
                    key={product.id}
                    onClick={() => goTo(product)}
                    onMouseEnter={() => setSelectedIdx(i)}
                    className={`w-full flex items-center gap-4 px-5 py-3 transition-colors ${
                      i === selectedIdx ? 'bg-[var(--gold)]/10' : 'hover:bg-white/5'
                    }`}
                    data-testid={`search-result-${i}`}
                  >
                    <span
                      className="w-10 h-10 rounded-lg flex-shrink-0 border border-[var(--text-primary)]/10"
                      style={{ backgroundColor: product.color_hex || '#333' }}
                    />
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm text-[var(--text-primary)] truncate">{product.color_name}</p>
                      <p className="text-[11px] text-[var(--text-secondary)]/60 truncate">
                        {product.texture_variant} &middot; {product.color_code} &middot; {product.available_lengths?.length} lengths
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-[var(--gold)]">${product.price_from}</span>
                      <ArrowRight className={`w-3.5 h-3.5 transition-opacity ${i === selectedIdx ? 'opacity-100 text-[var(--gold)]' : 'opacity-0'}`} />
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-2.5 border-t border-[var(--text-primary)]/5 bg-[var(--bg-base)]">
            <div className="flex items-center gap-3 text-[10px] text-[#666]">
              <span><kbd className="border border-[var(--text-primary)]/10 rounded px-1 py-0.5 mr-0.5">&uarr;</kbd><kbd className="border border-[var(--text-primary)]/10 rounded px-1 py-0.5">&darr;</kbd> navigate</span>
              <span><kbd className="border border-[var(--text-primary)]/10 rounded px-1 py-0.5">&#9166;</kbd> select</span>
            </div>
            <span className="text-[10px] text-[#666]">{catalog.length} products</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPalette;
